// TreatmentProcedure.jsx
import React, { useContext, useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import "../MoComponent.css";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Header from "../../Header/Header";
import { useLocation, useNavigate } from "react-router-dom";
import { apisPath, paths } from "../../../utils/path";
import { CommonContext } from "../../../context/CommonContext";
import { authHttp } from "../../../services/commonHttpService";
import PatientCard from "../../PatientCard/PatientCard";
import { formatDate } from "../../../helpers/commonHelper";
import { AuthContext } from "../../../context/AuthContext";
import { Row, Col, Form } from "react-bootstrap";
import AppointmentSlotsCard from "../../PatientCard/AppointmentSlotsCard";
import AppointmentSlots from "../../PatientCard/AppointmentSlots";
import ChemotherapyProgress from "../../PatientCard/ChemotherapyProgress";

const TreatmentProcedure = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setIsLoading, setAlertState } = useContext(CommonContext);
  const { userRole } = useContext(AuthContext);

  const patientId = location.state?.patientId;
  const planned_chemo_cycles = location.state?.planned_chemo_cycles;
  const upcoming_on_min = location.state?.upcoming_on_min;
  const patientName = location.state?.patientName;
  const [expandedAccordion, setExpandedAccordion] = useState("panel1");

  // State for each step data
  const [step1Data, setStep1Data] = useState(null);
  const [step2Data, setStep2Data] = useState(null);
  const [step3Data, setStep3Data] = useState(null);
  const [step4Data, setStep4Data] = useState(null);
  const [step5Data, setStep5Data] = useState(null);
  const [moChemoData, setMoChemoData] = useState(null);
  const [openMaster, setOpenMaster] = useState(false);

  // Fetch treatment steps data
  const fetchTreatmentSteps = async () => {
    if (!patientId) return;

    setIsLoading(true);
    try {
      const response = await authHttp.get(
        `${apisPath?.getPatientTreatmentStep}/${patientId}`
      );

      if (response?.data) {
        setStep1Data(response?.data?.MI?.step1?.data);
        setStep2Data(response?.data?.MI?.step2?.data);
        setStep3Data(response?.data?.MI?.step3?.data);
        setStep4Data(response?.data?.MI?.step4?.data);
        setStep5Data(response?.data?.MI?.step5?.data);
        setMoChemoData(response?.data?.MO?.chemo);
      }
    } catch (error) {
      setAlertState({
        open: true,
        title:
          error?.response?.data?.message || "Failed to fetch treatment data",
        btnTxt: "CLOSE",
        status: "fail",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fields = [
    "Date of Checkup",
    "ECOG PS",
    "Hemoglobin",
    "Total Leukocyte",
    "Absolute Neutrophil",
    "Platelet Count",
    "Serum Creatinine",
    "Blood Urea Nitrogen",
    "Serum Bilirubin",
    "SGOT",
    "SGPT",
    "Sodium",
    "Potassium",
    "Cancer Markers",
  ];
  const chemoFields = [
    { label: "Date of Administration", key: "referralDate" },
    { label: "Cycle Number", key: "cycleNumber" },
    { label: "Chemotherapy Drugs and Dosage", key: "chemoDrugName" },
    { label: "Infusion Rate / Duration", key: "chemoInfusionRateDuration" },
    { label: "Diluent / Volume", key: "chemoDiluentVolume" },
    { label: "Timing", key: "chemoTiming" },
    { label: "Delivery Method", key: "chemoDeliveryMethod" },
    {
      label: "Notes / Special Instructions",
      key: "chemoNotesSpecialInstructions",
    },
    {
      label: "Significant Side Effects Reported",
      key: "significantSideEffects",
    },
    { label: "Medicines Prescribed", key: "medicinePrescribed" },
    { label: "Discharge Summary", key: "dischargeSummary" },
    { label: "Name of Doctor", key: "doctorName" },
  ];

  useEffect(() => {
    if (!patientId) {
      setAlertState({
        open: true,
        title:
          "Patient ID not found. Please select a patient from the beneficiary list.",
        btnTxt: "GO BACK",
        status: "fail",
      });
      navigate(
        `${paths?.user?.commonPoint}${paths?.user?.beneficiaryManagement}`
      );
      return;
    }

    fetchTreatmentSteps();
  }, [patientId]);

  const handleInitiateTreatment = () => {
    navigate(
      `${paths?.user?.commonPoint}${paths?.user?.treatmentProcedureStep}`,
      {
        state: {
          patientId,
          step5Data,
          planned_chemo_cycles,
          upcoming_on_min,
        },
      }
    );
  };

  // Handle accordion change - only one open at a time
  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpandedAccordion(isExpanded ? panel : false);
  };
  // Handelar of master Accordian -----------------
  const handleMasterChange = (panel) => (event, isExpanded) => {
    setOpenMaster(isExpanded ? panel : false);
  };

  return (
    <>
      <Header showFullHeader={false} />
      <Container className="tp-container">
        {/* ---------- PAGE HEADER ---------- */}
        <Box className="tp-header">
          <Typography className="tp-title">
            Treatment & Procedure Module
          </Typography>

          {userRole === 5 && (
            <button
              className="tp-initiate-btn"
              onClick={handleInitiateTreatment}
            >
              Initiate Treatment
            </button>
          )}
        </Box>

        {/* ---------- PATIENT CARD ---------- */}
        <Box className="row">
          {/* LEFT */}
          <Box className="col-md-3 col-12 mb-3 mb-md-0">
            <PatientCard patientId={patientId} />
          </Box>

          {/* RIGHT */}

          <AppointmentSlotsCard
            patientId={patientId}
            planned_chemo_cycles={planned_chemo_cycles}
            patientName={patientName}
            handleInitiateTreatment={handleInitiateTreatment}
          />
          {/* <Box className="col-md-4 col-12 mb-3 mb-md-0">
            <ChemotherapyProgress />
          </Box>
          <Box className="col-md-5 col-12 mb-3 mb-md-0">
            <AppointmentSlots />
          </Box> */}
        </Box>

        {/* ---------------------------------------------------------------- */}
        {/* ----------------------- ACCORDIONS START ------------------------ */}
        {/* ---------------------------------------------------------------- */}
        <Accordion
          expanded={openMaster === "master1"}
          onChange={handleMasterChange("master1")}
          className="custom-acc master-acc"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className="tp-section-title">
              Treatment & Clinical Summary From TCCC
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            {/* ---------- SECTION 1 ---------- */}
            <Accordion
              expanded={expandedAccordion === "panel1"}
              onChange={handleAccordionChange("panel1")}
              className="custom-acc"
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography className="tp-section-title">
                  Diagnostic Details
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                <div className="upload-clinical">
                  {/* Section Title */}

                  {/* Date Field */}
                  <div className="row">
                    <div className="col-md-3 col-12 mb-3">
                      <label className="form-label">Date of diagnostic</label>
                      <p className="view-value">
                        {formatDate(step1Data?.date_of_diagnosis)}
                      </p>
                    </div>
                  </div>

                  {/* Cancer Info Row */}
                  <div className="row mb-4">
                    <div className="col-md-3 col-12 mb-3">
                      <label className="form-label">
                        Primary Site of Tumour/Topography
                      </label>
                      <p className="view-value">
                        {step1Data?.primary_tumour_site || "N/A"}
                      </p>
                    </div>

                    <div className="col-md-2 col-12 mb-3">
                      <label className="form-label">Histology/Morphology</label>
                      <p className="view-value">
                        {step1Data?.histology_morphology_description || "N/A"}
                      </p>
                    </div>

                    <div className="col-md-3 col-12 mb-3">
                      <label className="form-label">
                        Coding According to ICD-O-3
                      </label>
                      <p className="view-value">
                        {step1Data?.icdo3_coding || "N/A"}
                      </p>
                    </div>

                    <div className="col-md-3 col-12 mb-3">
                      <label className="form-label">
                        Secondary Site of Tumour
                      </label>
                      <p className="view-value">
                        {step1Data?.secondary_tumour_site || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Morphology + Laterality */}
                  <div className="row mb-4 align-items-center">
                    {/* Morphology of Metastasis */}
                    <div className="col-md-3 col-12 mb-3">
                      <label className="form-label">
                        Morphology of Metastasis
                      </label>
                      <p className="view-value">
                        {step1Data?.metastasis_morphology || "—"}
                      </p>
                    </div>

                    {/* Laterality Selection */}
                    <div className="col-md-4 col-12 mb-3">
                      <label className="form-label fw-semibold d-block mb-2">
                        Laterality
                      </label>
                      <p className="view-value">
                        {step1Data?.is_laterality === 1 ? "Yes" : "No"}
                      </p>
                    </div>

                    {/* Laterality Dropdown Value */}
                    {step1Data?.is_laterality === 1 && (
                      <div className="col-md-3 col-12 mb-3">
                        <label className="form-label">Laterality Details</label>
                        <p className="view-value">
                          {step1Data?.laterality || "N/A"}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>

            {/* ---------- SECTION 2: Clinical Stage ---------- */}
            <Accordion
              expanded={expandedAccordion === "panel2"}
              onChange={handleAccordionChange("panel2")}
              className="custom-acc"
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography className="tp-section-title">
                  Clinical Stage
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <div className="upload-clinical">
                  {/* Title */}
                  {/* Clinical Extent of Disease */}
                  <div className="row mb-4 align-items-center">
                    <div className="col-md-3 col-12 mt-4">
                      <label className="form-label">
                        Clinical Extent of Disease
                      </label>
                      <p className="view-value">
                        {step2Data?.disease_extent || "N/A"}
                      </p>
                    </div>

                    {/* If “Others” Selected */}
                    {step2Data?.disease_extent === "Others" && (
                      <div className="col-md-4 col-12">
                        <label className="form-label"></label>
                        <p className="view-value">
                          {step2Data?.disease_extent_other || "N/A"}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* ---------- STAGING SYSTEM ---------- */}
                  <h5 className="mb-3">Staging System</h5>

                  {/* Clinical Stage Group */}
                  <Typography variant="p" sx={{ fontWeight: 400 }}>
                    Clinical Stage Group
                  </Typography>

                  <div className="row">
                    <div className="col-md-2 col-lg-3 col-12">
                      <label className="form-label">T (Tumor)</label>
                      <p className="view-value">
                        {step2Data?.clinical_t || "N/A"}
                      </p>
                    </div>

                    <div className="col-md-2 col-lg-3 col-12">
                      <label className="form-label">N (Nodes)</label>
                      <p className="view-value">
                        {step2Data?.clinical_n || "N/A"}
                      </p>
                    </div>

                    <div className="col-md-2 col-lg-3 col-12">
                      <label className="form-label">M (Metastasis)</label>
                      <p className="view-value">
                        {step2Data?.clinical_m || "N/A"}
                      </p>
                    </div>
                  </div>

                  {/* Pathological Stage Group */}
                  <div className="row mt-5">
                    <Typography variant="p" sx={{ fontWeight: 400 }}>
                      Pathological Stage Group
                    </Typography>

                    <div className="col-md-2 col-lg-3 col-12">
                      <label className="form-label">T (Tumor)</label>
                      <p className="view-value">
                        {step2Data?.pathological_t || "N/A"}
                      </p>
                    </div>

                    <div className="col-md-2 col-lg-3 col-12">
                      <label className="form-label">N (Nodes)</label>
                      <p className="view-value">
                        {step2Data?.pathological_n || "N/A"}
                      </p>
                    </div>

                    <div className="col-md-2 col-lg-3 col-12">
                      <label className="form-label">M (Metastasis)</label>
                      <p className="view-value">
                        {step2Data?.pathological_m || "N/A"}
                      </p>
                    </div>
                  </div>
                </div>
              </AccordionDetails>
            </Accordion>

            {/* ---------- SECTION 3: Chemotherapy Administration Details ---------- */}
            <Accordion
              expanded={expandedAccordion === "panel3"}
              onChange={handleAccordionChange("panel3")}
              className="custom-acc"
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography className="tp-section-title">
                  Chemotherapy Administration Details
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                <Box className="tp-section">
                  {/* ECOG Status */}
                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>ECOG Status</label>
                      <p className="view-value">
                        {step3Data?.ecog_status || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Start Date</label>
                      <p className="view-value">
                        {formatDate(step3Data?.date_of_start)}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>End Date</label>
                      <p className="view-value">
                        {formatDate(step3Data?.date_of_end)}
                      </p>
                    </div>
                  </Box>

                  {/* Intention / Role / Details */}
                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Intention to Treatment</label>
                      <p className="view-value">
                        {step3Data?.intention_of_treatment || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Role of Treatment</label>
                      <p className="view-value">
                        {step3Data?.role_of_treatment || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Details</label>
                      <p className="view-value">
                        {step3Data?.details || "N/A"}
                      </p>
                    </div>
                  </Box>

                  {/* Status */}
                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Status of Treatment</label>
                      <p className="view-value">
                        {step3Data?.status_of_treatment || "N/A"}
                      </p>
                    </div>
                  </Box>

                  {/* Surgery */}
                  <h5 className="my-3">Surgery</h5>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Date of Surgery</label>
                      <p className="view-value">
                        {formatDate(step3Data?.date_of_surgery)}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Primary Site</label>
                      <p className="view-value">
                        {step3Data?.primary_site || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Surgeon Name</label>
                      <p className="view-value">
                        {step3Data?.surgeon_name || "N/A"}
                      </p>
                    </div>
                  </Box>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Surgical Procedure Performed</label>
                      <p className="view-value">
                        {step3Data?.surgical_procedure_performed || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Extent Resection</label>
                      <p className="view-value">
                        {step3Data?.extent_of_resection || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Tumour Margins</label>
                      <p className="view-value">
                        {step3Data?.tumor_margins || "N/A"}
                      </p>
                    </div>
                  </Box>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Lymph Node Dissection</label>
                      <p className="view-value">
                        {step3Data?.lymph_node_dissection || "N/A"}
                      </p>
                    </div>
                  </Box>

                  {/* Chemotherapy */}
                  <h5 className="my-3">Chemotherapy</h5>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Regimen</label>
                      <p className="view-value">
                        {step3Data?.chemo_regimen || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Protocol with Duration</label>
                      <p className="view-value">
                        {step3Data?.chemo_protocol_with_duration || "N/A"}
                      </p>
                    </div>
                  </Box>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Number of Cycles Planned</label>
                      <p className="view-value">
                        {step3Data?.chemo_planned_cycles || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Number of Cycles Administered</label>
                      <p className="view-value">
                        {step3Data?.chemo_cycles_administered || "N/A"}
                      </p>
                    </div>
                  </Box>

                  {/* Radiotherapy */}
                  <h5 className="my-3">Radiotherapy</h5>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Regimen</label>
                      <p className="view-value">
                        {step3Data?.radiotherapy_regimen || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Protocol Name</label>
                      <p className="view-value">
                        {step3Data?.radiotherapy_protocol_name || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Treatment Intent</label>
                      <p className="view-value">
                        {step3Data?.radiotherapy_treatment_intent || "N/A"}
                      </p>
                    </div>
                  </Box>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Total Dose</label>
                      <p className="view-value">
                        {step3Data?.radiotherapy_total_dose || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Dose per Fraction</label>
                      <p className="view-value">
                        {step3Data?.radiotherapy_dose_per_fraction || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Treatment Site</label>
                      <p className="view-value">
                        {step3Data?.radiotherapy_treatment_site || "N/A"}
                      </p>
                    </div>
                  </Box>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Number of Fractions Planned</label>
                      <p className="view-value">
                        {step3Data?.radiotherapy_no_of_planned_fractions ||
                          "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Number of Fractions Administered</label>
                      <p className="view-value">
                        {step3Data?.radiotherapy_no_of_fractions_administered ||
                          "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Radiation Modality</label>
                      <p className="view-value">
                        {step3Data?.radiotherapy_radiation_modality || "N/A"}
                      </p>
                    </div>
                  </Box>

                  {/* Targeted Therapy */}
                  <h5 className="my-3">Targeted Therapy</h5>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Regimen/Protocol Name</label>
                      <p className="view-value">
                        {step3Data?.targeted_therapy_regimen_protocol || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Treatment Intent</label>
                      <p className="view-value">
                        {step3Data?.targeted_therapy_treatment_intent || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Treatment Drug</label>
                      <p className="view-value">
                        {step3Data?.targeted_therapy_targeted_drug || "N/A"}
                      </p>
                    </div>
                  </Box>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Molecular Target</label>
                      <p className="view-value">
                        {step3Data?.targeted_therapy_molecular_target || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Specific Dose</label>
                      <p className="view-value">
                        {step3Data?.targeted_therapy_specific_dose || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Route of Administration</label>
                      <p className="view-value">
                        {step3Data?.targeted_therapy_route_of_administration ||
                          "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Cycles (Planned)</label>
                      <p className="view-value">
                        {step3Data?.targeted_therapy_planned_cycles || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Route of Administration</label>
                      <p className="view-value">
                        {step3Data?.targeted_therapy_administered_cycles ||
                          "N/A"}
                      </p>
                    </div>
                  </Box>

                  {/* Immunotherapy */}
                  <h5 className=" my-3">Immunotherapy</h5>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Regimen</label>
                      <p className="view-value">
                        {step3Data?.immunotherapy_regimen || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Protocol Name</label>
                      <p className="view-value">
                        {step3Data?.immunotherapy_protocol_name || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Treatment Intent</label>
                      <p className="view-value">
                        {step3Data?.immunotherapy_treatment_intent || "N/A"}
                      </p>
                    </div>
                  </Box>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Immunotherapy Drug</label>
                      <p className="view-value">
                        {step3Data?.immunotherapy_targeted_drug || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Mechanism of Action</label>
                      <p className="view-value">
                        {step3Data?.immunotherapy_molecular_target || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Specific Dose</label>
                      <p className="view-value">
                        {step3Data?.immunotherapy_specific_dose || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Route of Administration</label>
                      <p className="view-value">
                        {step3Data?.immunotherapy_route_of_administration ||
                          "N/A"}
                      </p>
                    </div>
                  </Box>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Number of Cycles Planned</label>
                      <p className="view-value">
                        {step3Data?.immunotherapy_planned_cycles || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Number of Cycles Administered</label>
                      <p className="view-value">
                        {step3Data?.immunotherapy_administered_cycles || "N/A"}
                      </p>
                    </div>
                  </Box>

                  {/* Hormone Therapy */}
                  <h5 className="my-3">Hormone Therapy</h5>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Regimen/Protocol Name</label>
                      <p className="view-value">
                        {step3Data?.hormone_therapy_regimen_protocol || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Treatment Intent</label>
                      <p className="view-value">
                        {step3Data?.hormone_therapy_treatment_intent || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Hormone Therapy Drug</label>
                      <p className="view-value">
                        {step3Data?.hormone_therapy_immunotherapy || "N/A"}
                      </p>
                    </div>
                  </Box>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Mechanism of Action</label>
                      <p className="view-value">
                        {step3Data?.hormone_therapy_mechanism_of_action ||
                          "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Specific Dose</label>
                      <p className="view-value">
                        {step3Data?.hormone_therapy_specific_dose || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Route of Administration</label>
                      <p className="view-value">
                        {step3Data?.hormone_therapy_route_of_administration ||
                          "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Cycles Planned</label>
                      <p className="view-value">
                        {step3Data?.hormone_therapy_planned_cycles || "N/A"}
                      </p>
                    </div>
                  </Box>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Cycles Administered</label>
                      <p className="view-value">
                        {step3Data?.hormone_therapy_cycles_administered ||
                          "N/A"}
                      </p>
                    </div>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* ---------- SECTION 4: Premedications ---------- */}
            <Accordion
              expanded={expandedAccordion === "panel4"}
              onChange={handleAccordionChange("panel4")}
              className="custom-acc"
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography className="tp-section-title">
                  Premedications
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                <Box className="tp-section">
                  {/* TITLE */}
                  <h5>Current Chemotherapy Protocol</h5>

                  {/* ROW 1 */}
                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Chemotherapy Regimen Details</label>
                      <p className="view-value">
                        {step4Data?.chemo_regimen_details || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Intent</label>
                      <p className="view-value">
                        {step4Data?.treatment_intent || "N/A"}
                      </p>
                    </div>
                  </Box>

                  {/* TITLE */}
                  <h5>Treatment Provided</h5>

                  {/* ---- ROW 1 ---- */}
                  {step4Data?.treatments.map((data, idx) => (
                    <Box className="tp-row" key={idx + 1}>
                      <div className="tp-col-cust">
                        <label>Date</label>
                        <p className="view-value">
                          {formatDate(data?.chemo_administered_date)}
                        </p>
                      </div>

                      <div className="tp-col-cust">
                        <label>Dose {idx + 1}</label>
                        <p className="view-value">{data?.chemo_dosage}</p>
                      </div>
                    </Box>
                  ))}

                  {/* TITLE */}
                  <h5>Drug Allergies & Access</h5>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Known Drug Allergies</label>
                      <p className="view-value">
                        {step4Data?.known_drug_allergies || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Vascular Access</label>
                      <p className="view-value">
                        {step4Data?.vascular_access || "N/A"}
                      </p>
                    </div>
                  </Box>

                  {/* TITLE */}
                  <h5>Adverse Reactions</h5>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Significant Adverse Reactions Reported</label>
                      <p className="view-value">
                        {step4Data?.significant_adverse_reactions_reported ||
                          "N/A"}
                      </p>
                    </div>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>

            {/* ---------- SECTION 5: Instructions for DCCC ---------- */}
            <Accordion
              expanded={expandedAccordion === "panel5"}
              onChange={handleAccordionChange("panel5")}
              className="custom-acc"
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography className="tp-section-title">
                  Instruction for DCCC while referring Patients
                </Typography>
              </AccordionSummary>

              <AccordionDetails>
                <Box className="tp-section">
                  {/* Date of Referral */}
                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Date of Referral</label>
                      <p className="view-value">
                        {formatDate(step5Data?.referral_date)}
                      </p>
                    </div>
                  </Box>

                  {/* Referred to DCCC */}
                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>State</label>
                      <p className="view-value">
                        {step5Data?.state_name?.state_name}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>District</label>
                      <p className="view-value">
                        {step5Data?.district_name?.district_name}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>DCCC</label>
                      <p className="view-value">
                        {step5Data?.dccc_name?.dccc_name}
                      </p>
                    </div>
                  </Box>

                  {/* TITLE */}
                  <h5>Chemotherapy Drugs for Upcoming Cycles</h5>
                  {/* Chemotherapy Drugs */}
                  {step5Data?.drug_details.map((data, idx) => (
                    <Box className="tp-row" key={idx}>
                      <div className="tp-col-cust">
                        <label>Drug Name</label>
                        <p className="view-value">{data?.drug_name || "N/A"}</p>
                      </div>

                      <div className="tp-col-cust">
                        <label>Dosage (per Kg/flat dose)</label>
                        <p className="view-value">{data?.dosage || "N/A"}</p>
                      </div>

                      <div className="tp-col-cust">
                        <label>Final Calculated Dose</label>
                        <p className="view-value">
                          {data?.final_dose || "N/A"}
                        </p>
                      </div>

                      <div className="tp-col-cust">
                        <label>Route of Administration</label>
                        <p className="view-value">
                          {data?.route_admin || "N/A"}
                        </p>
                      </div>
                    </Box>
                  ))}

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Number of Cycles Planned</label>
                      <p className="view-value">
                        {step5Data?.planned_chemo_cycles || "N/A"}
                      </p>
                    </div>
                  </Box>

                  {/* Chemotherapy Administration Details */}
                  <h5>Chemotherapy Administration Details</h5>
                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Infusion solution/dilution</label>
                      <p className="view-value">
                        {step5Data?.chemo_infusion_rate_duration || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Diluent volume</label>
                      <p className="view-value">
                        {step5Data?.chemo_diluent_volume || "N/A"}
                      </p>
                    </div>
                  </Box>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Timing</label>
                      <p className="view-value">
                        {step5Data?.chemo_timing || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Delivery Method</label>
                      <p className="view-value">
                        {step5Data?.chemo_delivery_method || "N/A"}
                      </p>
                    </div>
                  </Box>

                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Notes/Special Instructions</label>
                      <p className="view-value">
                        {step5Data?.chemo_notes_special_instructions || "N/A"}
                      </p>
                    </div>
                  </Box>

                  {/* Premedications */}
                  <h5>Premedications</h5>

                  {step5Data?.premedication_details.map((data, idx) => (
                    <Box className="tp-row" key={idx}>
                      <div className="tp-col-cust">
                        <label>Drug Name</label>
                        <p className="view-value">{data?.drug_name || "N/A"}</p>
                      </div>

                      <div className="tp-col-cust">
                        <label>Dose</label>
                        <p className="view-value">{data?.drug_dose || "N/A"}</p>
                      </div>

                      <div className="tp-col-cust">
                        <label>Route of Administration</label>
                        <p className="view-value">
                          {data?.administration_route || "N/A"}
                        </p>
                      </div>
                    </Box>
                  ))}

                  {/* Hydration Plan */}
                  <h5>Hydration Plan</h5>
                  <h6>Pre-Chemo Hydration</h6>
                  {/* Pre-Chemo Hydration */}
                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Fluid Type</label>
                      <p className="view-value">
                        {step5Data?.pre_chemo_hydration_fluid_type || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Volume</label>
                      <p className="view-value">
                        {step5Data?.pre_chemo_hydration_fluid_volume || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Duration</label>
                      <p className="view-value">
                        {step5Data?.pre_chemo_hydration_fluid_duration || "N/A"}
                      </p>
                    </div>
                  </Box>

                  {/* Post-Chemo Hydration */}
                  <h6>Post-Chemo Hydration</h6>
                  <Box className="tp-row">
                    <div className="tp-col-cust">
                      <label>Post-Chemo Hydration Type</label>
                      <p className="view-value">
                        {step5Data?.post_chemo_hydration_fluid_type || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Volume</label>
                      <p className="view-value">
                        {step5Data?.post_chemo_hydration_fluid_volume || "N/A"}
                      </p>
                    </div>

                    <div className="tp-col-cust">
                      <label>Duration</label>
                      <p className="view-value">
                        {step5Data?.post_chemo_hydration_fluid_duration ||
                          "N/A"}
                      </p>
                    </div>
                  </Box>
                </Box>
              </AccordionDetails>
            </Accordion>
          </AccordionDetails>
        </Accordion>
        {/* -------------Section 6 Slots Details ---------------------- */}
        {/* <Accordion
          expanded={expandedAccordion === "panelSlots"}
          onChange={handleAccordionChange("panelSlots")}
          className="custom-acc"
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography className="tp-section-title fw-semibold">
              Slots Details 25-11-25
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <div className="row px-3 py-2">

              {fields.map((label, i) => (
                <div className="col-md-4 col-12 mb-3" key={i}>
                  <Typography
                    sx={{ fontSize: "14px", fontWeight: 500, color: "#555" }}
                  >
                    {label}
                  </Typography>

                  <Box
                    sx={{
                      borderBottom: "1px solid #000",
                      paddingBottom: "6px",
                      fontSize: "15px",
                      minHeight: "28px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >

                    <span style={{ color: "#000" }}>—</span>
                  </Box>
                </div>
              ))}

            </div>
            <Typography sx={{ fontSize: "15px", fontWeight: 600, mb: 2 }}>
              Chemotherapy Administration Details
            </Typography>

            <div className="row px-3 py-2">

              {chemoFields.map((item, index) => (
                <div className="col-md-4 col-12 mb-4" key={index}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 500, color: "#444" }}>
                    {item.label}
                  </Typography>

                  <Box
                    sx={{
                      borderBottom: "1px solid #000",
                      paddingBottom: "6px",
                      minHeight: "28px",
                      fontSize: "15px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >

                    ----
                  </Box>
                </div>
              ))}

            </div>


            <Typography sx={{ fontSize: "15px", fontWeight: 600, mt: 3 }}>
              Drug Details
            </Typography>

            <div className="row px-2 py-2">
              {[1, 2].map((item, i) => ( // simulate rows - replace later
                <div key={i} className="row mb-3">
                  <div className="col-md-4 col-12">
                    <Typography sx={{ fontSize: "14px" }}>Drug Name</Typography>
                    <Box sx={{ borderBottom: "1px solid #000", minHeight: "26px" }}>----</Box>
                  </div>

                  <div className="col-md-4 col-12">
                    <Typography sx={{ fontSize: "14px" }}>Dose Administered</Typography>
                    <Box sx={{ borderBottom: "1px solid #000", minHeight: "26px" }}>----</Box>
                  </div>

                  <div className="col-md-4 col-12">
                    <Typography sx={{ fontSize: "14px" }}>Route of Administration</Typography>
                    <Box sx={{ borderBottom: "1px solid #000", minHeight: "26px" }}>----</Box>
                  </div>
                </div>
              ))}
            </div>


            <Typography sx={{ fontSize: "15px", fontWeight: 600, mt: 4 }}>
              Premedications Given
            </Typography>

            <div className="row px-2 py-2">
              {[1].map((item, i) => ( // simulate row
                <div key={i} className="row mb-3">
                  <div className="col-md-4 col-12">
                    <Typography sx={{ fontSize: "14px" }}>Drug Name</Typography>
                    <Box sx={{ borderBottom: "1px solid #000", minHeight: "26px" }}>----</Box>
                  </div>

                  <div className="col-md-4 col-12">
                    <Typography sx={{ fontSize: "14px" }}>Dose Administered</Typography>
                    <Box sx={{ borderBottom: "1px solid #000", minHeight: "26px" }}>----</Box>
                  </div>

                  <div className="col-md-4 col-12">
                    <Typography sx={{ fontSize: "14px" }}>Route of Administration</Typography>
                    <Box sx={{ borderBottom: "1px solid #000", minHeight: "26px" }}>----</Box>
                  </div>
                </div>
              ))}
            </div>

          </AccordionDetails>
        </Accordion> */}

        {/* =================================================== */}

        {/* MO */}
        {moChemoData && Object.keys(moChemoData).length > 0 && (
          <>
            {Object.entries(moChemoData).map(([chemoKey, chemoData], idx) => {
              const preChemoData = chemoData?.PreChemo;
              const postChemoData = chemoData?.PostChemo;

              return (
                <Accordion
                  key={chemoKey}
                  expanded={expandedAccordion === `chemo-${chemoKey}`}
                  onChange={handleAccordionChange(`chemo-${chemoKey}`)}
                  className="custom-acc"
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography className="tp-section-title">
                      Chemo Session {idx + 1} 
                      {/* ({formatDate(preChemoData?.created_at)}) */}
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Box className="tp-section">
                      {/* ============================================== */}
                      {/* PRE-CHEMO CHECK-UP SECTION */}
                      {/* ============================================== */}
                      {preChemoData && (
                        <>
                          <h5 className="mb-3">Pre-Chemo Check-up</h5>
                          <div className="upload-clinical">
                            {/* Date and ECOG Status */}
                            <Row className="mb-3">
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Date of Check Up</Form.Label>
                                <p className="view-value">
                                  {formatDate(preChemoData?.check_up_date)}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>ECOG PS (0/1/2/3/4/5)</Form.Label>
                                <p className="view-value">
                                  {preChemoData?.ecog_ps || "N/A"}
                                </p>
                              </Col>
                            </Row>

                            {/* Recent Lab Results Section */}
                            <h6 className="mt-4 mb-3">Recent Lab Results</h6>

                            {/* Haemoglobin */}
                            <Row className="mb-3">
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Haemoglobin (g/dL)</Form.Label>
                                <p className="view-value">
                                  {preChemoData?.haemoglobin || "N/A"}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Date</Form.Label>
                                <p className="view-value">
                                  {formatDate(preChemoData?.haemoglobin_date)}
                                </p>
                              </Col>
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>
                                  Total Leucocyte Count (cells/mm³)
                                </Form.Label>
                                <p className="view-value">
                                  {preChemoData?.leucocyte || "N/A"}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Date</Form.Label>
                                <p className="view-value">
                                  {formatDate(preChemoData?.leucocyte_date)}
                                </p>
                              </Col>
                            </Row>

                            {/* Absolute Neutrophil Count */}
                            <Row className="mb-3">
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>
                                  Absolute Neutrophil Count (ANC)
                                </Form.Label>
                                <p className="view-value">
                                  {preChemoData?.neutrophil || "N/A"}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Date</Form.Label>
                                <p className="view-value">
                                  {formatDate(preChemoData?.neutrophil_date)}
                                </p>
                              </Col>
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>
                                  Platelet Count (lakh/μL)
                                </Form.Label>
                                <p className="view-value">
                                  {preChemoData?.platelet || "N/A"}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Date</Form.Label>
                                <p className="view-value">
                                  {formatDate(preChemoData?.platelet_date)}
                                </p>
                              </Col>
                            </Row>

                            {/* Serum Creatinine */}
                            <Row className="mb-3">
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>
                                  Serum Creatinine (mg/dL)
                                </Form.Label>
                                <p className="view-value">
                                  {preChemoData?.serum_creatinine || "N/A"}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Date</Form.Label>
                                <p className="view-value">
                                  {formatDate(
                                    preChemoData?.serum_creatinine_date
                                  )}
                                </p>
                              </Col>
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Blood Urea Nitrogen</Form.Label>
                                <p className="view-value">
                                  {preChemoData?.blood_urea_nitrogen || "N/A"}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Date</Form.Label>
                                <p className="view-value">
                                  {formatDate(
                                    preChemoData?.blood_urea_nitrogen_date
                                  )}
                                </p>
                              </Col>
                            </Row>

                            {/* Serum Bilirubin */}
                            <Row className="mb-3">
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Serum Bilirubin</Form.Label>
                                <p className="view-value">
                                  {preChemoData?.serum_bilirubin || "N/A"}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Date</Form.Label>
                                <p className="view-value">
                                  {formatDate(
                                    preChemoData?.serum_bilirubin_date
                                  )}
                                </p>
                              </Col>
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>SGOT</Form.Label>
                                <p className="view-value">
                                  {preChemoData?.sgot || "N/A"}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Date</Form.Label>
                                <p className="view-value">
                                  {formatDate(preChemoData?.sgot_date)}
                                </p>
                              </Col>
                            </Row>

                            {/* SGPT */}
                            <Row className="mb-3">
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>SGPT</Form.Label>
                                <p className="view-value">
                                  {preChemoData?.sgpt || "N/A"}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Date</Form.Label>
                                <p className="view-value">
                                  {formatDate(preChemoData?.sgpt_date)}
                                </p>
                              </Col>
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Sodium</Form.Label>
                                <p className="view-value">
                                  {preChemoData?.sodium || "N/A"}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Date</Form.Label>
                                <p className="view-value">
                                  {formatDate(preChemoData?.sodium_date)}
                                </p>
                              </Col>
                            </Row>

                            {/* Potassium */}
                            <Row className="mb-3">
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Potassium</Form.Label>
                                <p className="view-value">
                                  {preChemoData?.potassium || "N/A"}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Date</Form.Label>
                                <p className="view-value">
                                  {formatDate(preChemoData?.potassium_date)}
                                </p>
                              </Col>
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Cancer markers</Form.Label>
                                <p className="view-value">
                                  {preChemoData?.cancer_markers || "N/A"}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Date</Form.Label>
                                <p className="view-value">
                                  {formatDate(
                                    preChemoData?.cancer_markers_date
                                  )}
                                </p>
                              </Col>
                            </Row>
                          </div>
                        </>
                      )}

                      {/* ============================================== */}
                      {/* POST-CHEMO DETAILS SECTION */}
                      {/* ============================================== */}
                      {postChemoData && (
                        <>
                          <h5 className="mb-3 mt-4">Post-Chemo Details</h5>
                          <div className="upload-clinical">
                            {/* Chemotherapy Administration Details */}
                            <h6 className="mb-3">
                              Chemotherapy Administration Details
                            </h6>

                            <Row className="mb-3">
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Date of administration</Form.Label>
                                <p className="view-value">
                                  {formatDate(postChemoData?.referral_date)}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Cycle Number</Form.Label>
                                <p className="view-value">
                                  {postChemoData?.cycle_number || "N/A"}
                                </p>
                              </Col>
                            </Row>

                            {/* Chemotherapy Drugs and Dosage administered */}
                            <h6 className="mt-4 mb-3">
                              Chemotherapy Drugs and Dosage administered
                            </h6>

                            {postChemoData?.drug_mo_details &&
                              postChemoData.drug_mo_details.length > 0 ? (
                              postChemoData.drug_mo_details.map((drug, idx) => (
                                <Row className="mb-3" key={idx}>
                                  <Col md={3} sm={6} xs={12}>
                                    <Form.Label>Drug Name</Form.Label>
                                    <p className="view-value">
                                      {drug?.drug_name || "N/A"}
                                    </p>
                                  </Col>

                                  <Col md={3} sm={6} xs={12}>
                                    <Form.Label>Dose administered</Form.Label>
                                    <p className="view-value">
                                      {drug?.dose_administered || "N/A"}
                                    </p>
                                  </Col>

                                  <Col md={3} sm={6} xs={12}>
                                    <Form.Label>
                                      Route of Administration
                                    </Form.Label>
                                    <p className="view-value">
                                      {drug?.administration_route || "N/A"}
                                    </p>
                                  </Col>
                                </Row>
                              ))
                            ) : (
                              <Row className="mb-3">
                                <Col md={3} sm={6} xs={12}>
                                  <p className="view-value">
                                    No drug details available
                                  </p>
                                </Col>
                              </Row>
                            )}

                            {/* Administration Details */}
                            <Row className="mb-3">
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Infusion Rate/Duration</Form.Label>
                                <p className="view-value">
                                  {postChemoData?.chemo_infusion_rate_duration ||
                                    "N/A"}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Diluent/Volume</Form.Label>
                                <p className="view-value">
                                  {postChemoData?.chemo_diluuent_volume ||
                                    "N/A"}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Timing</Form.Label>
                                <p className="view-value">
                                  {postChemoData?.chemo_timing || "N/A"}
                                </p>
                              </Col>
                            </Row>

                            <Row className="mb-3">
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Delivery Method</Form.Label>
                                <p className="view-value">
                                  {postChemoData?.chemo_delivery_method ||
                                    "N/A"}
                                </p>
                              </Col>
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>
                                  Notes/Special Instructions
                                </Form.Label>
                                <p className="view-value">
                                  {postChemoData?.chemo_notes_special_instructions ||
                                    "N/A"}
                                </p>
                              </Col>
                            </Row>

                            {/* Premedications Given */}
                            <h6 className="mt-4 mb-3">Premedications Given</h6>

                            {postChemoData?.premedication_mo_details &&
                              postChemoData.premedication_mo_details.length >
                              0 ? (
                              postChemoData.premedication_mo_details.map(
                                (premed, idx) => (
                                  <Row className="mb-3" key={idx}>
                                    <Col md={3} sm={6} xs={12}>
                                      <Form.Label>Drug Name</Form.Label>
                                      <p className="view-value">
                                        {premed?.drug_name || "N/A"}
                                      </p>
                                    </Col>

                                    <Col md={3} sm={6} xs={12}>
                                      <Form.Label>Dose administered</Form.Label>
                                      <p className="view-value">
                                        {premed?.dose_administered || "N/A"}
                                      </p>
                                    </Col>

                                    <Col md={3} sm={6} xs={12}>
                                      <Form.Label>
                                        Route of Administration
                                      </Form.Label>
                                      <p className="view-value">
                                        {premed?.administration_route || "N/A"}
                                      </p>
                                    </Col>
                                  </Row>
                                )
                              )
                            ) : (
                              <Row className="mb-3">
                                <Col md={3} sm={6} xs={12}>
                                  <p className="view-value">
                                    No premedication details available
                                  </p>
                                </Col>
                              </Row>
                            )}

                            {/* Hydration Given */}
                            <h6 className="mt-4 mb-3">Hydration Given</h6>

                            {/* Pre-Chemo Hydration */}
                            <h6 className="mb-2">Pre-Chemo Hydration</h6>
                            <Row className="mb-3">
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Fluid Type</Form.Label>
                                <p className="view-value">
                                  {postChemoData?.pre_chemo_hydration_fluid_type ||
                                    "N/A"}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Volume</Form.Label>
                                <p className="view-value">
                                  {postChemoData?.pre_chemo_hydratiom_fluid_volume ||
                                    "N/A"}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Duration</Form.Label>
                                <p className="view-value">
                                  {postChemoData?.pre_chemo_hydration_fluid_duration ||
                                    "N/A"}
                                </p>
                              </Col>
                            </Row>

                            {/* Post-Chemo Hydration */}
                            <h6 className="mt-3 mb-2">Post-Chemo Hydration</h6>
                            <Row className="mb-3">
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Fluid Type</Form.Label>
                                <p className="view-value">
                                  {postChemoData?.post_chemo_hydration_fluid_type ||
                                    "N/A"}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Volume</Form.Label>
                                <p className="view-value">
                                  {postChemoData?.post_chemo_hydratiom_fluid_volume ||
                                    "N/A"}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Duration</Form.Label>
                                <p className="view-value">
                                  {postChemoData?.post_chemo_hydration_fluid_duration ||
                                    "N/A"}
                                </p>
                              </Col>
                            </Row>

                            {/* Additional Details */}
                            <h6 className="mt-4 mb-3">Additional Details</h6>

                            <Row className="mb-3">
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>
                                  Any Significant Side Effects Reported
                                </Form.Label>
                                <p className="view-value">
                                  {postChemoData?.significant_side_effects ||
                                    "N/A"}
                                </p>
                              </Col>
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Medicine Prescribed</Form.Label>
                                <p className="view-value">
                                  {postChemoData?.medicine_prescribed || "N/A"}
                                </p>
                              </Col>
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Discharge Summary</Form.Label>
                                <p className="view-value">
                                  {postChemoData?.discharge_summary || "N/A"}
                                </p>
                              </Col>
                            </Row>
                            <Row className="mb-3">
                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Name of Doctor</Form.Label>
                                <p className="view-value">
                                  {postChemoData?.doctor_name || "N/A"}
                                </p>
                              </Col>

                              <Col md={3} sm={6} xs={12}>
                                <Form.Label>Mobile Number</Form.Label>
                                <p className="view-value">
                                  {postChemoData?.mobile_number || "N/A"}
                                </p>
                              </Col>
                            </Row>
                          </div>
                        </>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </>
        )}

        {/* ---------------------------------------------------------------- */}
        {/* ------------------------ ACCORDIONS END ------------------------- */}
        {/* ---------------------------------------------------------------- */}
      </Container>
    </>
  );
};

export default TreatmentProcedure;
